import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Collapse,
  Snackbar,
  Alert
} from '@mui/material';
import { Edit, Delete, Add, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { ICategory, listCategories, createCategory, updateCategory, deleteCategory } from '../../../services/informationPublicationsService';
import { useEntity } from '../../../context/EntityContext'; // Corrigido: usar o contexto da entidade
import CategoryForm, { ICategoryFormValues } from './CategoryForm';

// Função auxiliar para achatar a árvore de categorias para o seletor
const flattenCategoriesForSelect = (categories: ICategory[], level = 0): { id: string; name: string; level: number }[] => {
  let list: { id: string; name: string; level: number }[] = [];
  categories.forEach(cat => {
    list.push({ id: cat.id, name: cat.category, level });
    if (cat.children && cat.children.length > 0) {
      list = list.concat(flattenCategoriesForSelect(cat.children, level + 1));
    }
  });
  return list;
};

// Função auxiliar para obter os IDs de uma categoria e todos os seus descendentes
const getDescendantIds = (category: ICategory): string[] => {
  let ids = [category.id];
  if (category.children) {
    category.children.forEach(child => {
      ids = ids.concat(getDescendantIds(child));
    });
  }
  return ids;
};

interface ModalState {
  open: boolean;
  mode: 'create' | 'edit';
  category?: ICategory;
  parentId?: string | null;
}

interface CategoryNodeProps {
  category: ICategory;
  level: number;
  onEdit: (category: ICategory) => void;
  onDelete: (categoryId: string) => void;
  onAddSubcategory: (parentId: string) => void;
  scrollToId: string | null;
  expandedParentId: string | null;
  onScrolled: () => void;
}

// Componente foi movido para fora para evitar recriação em cada render
const CategoryNode: React.FC<CategoryNodeProps> = ({ category, level, onEdit, onDelete, onAddSubcategory, scrollToId, expandedParentId, onScrolled }) => {
  const [open, setOpen] = useState(false);
  const elementRef = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    // Se esta categoria for o pai que precisa ser expandido, abra-a.
    if (category.id === expandedParentId) {
      setOpen(true);
    }
  }, [expandedParentId, category.id]);

  React.useEffect(() => {
    if (category.id === scrollToId && elementRef.current) {
      elementRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      onScrolled(); // Limpa o ID para não rolar novamente em re-renderizações
    }
  }, [category.id, scrollToId, onScrolled]);

  const handleToggle = () => {
    if (category.children && category.children.length > 0) {
      setOpen(!open);
    }
  };

  return (
    <>
      <ListItem ref={elementRef} sx={{ pl: 2 * level, display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee' }}>
        {category.children && category.children.length > 0 ? (
          <IconButton onClick={handleToggle} size="small" sx={{ mr: 1 }}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        ) : <Box sx={{ width: 40, mr: 1 }} /> /* Espaçador para alinhamento */}
        <ListItemText 
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {category.icon && <Icon icon={category.icon} />}
              {category.category}
            </Box>
          } 
          secondary={category.description} 
          sx={{ flexGrow: 1 }}
        />
        <Box>
          <Button variant="text" size="small" startIcon={<Add />} onClick={() => onAddSubcategory(category.id)}>
            Adicionar Subcategoria
          </Button>
          <IconButton size="small" onClick={() => onEdit(category)}><Edit /></IconButton>
          <IconButton size="small" onClick={() => onDelete(category.id)}><Delete /></IconButton>
        </Box>
      </ListItem>
      {category.children && category.children.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {category.children.map((child) => (
              <CategoryNode 
                key={child.id} 
                category={child} 
                level={level + 1}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddSubcategory={onAddSubcategory}
                scrollToId={scrollToId}
                expandedParentId={expandedParentId}
                onScrolled={onScrolled}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const InformationCategories: React.FC = () => {
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { selectedEntity } = useEntity(); // Corrigido: Acessar a entidade pelo contexto
  const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'error' } | null>(null);
  const [modalState, setModalState] = useState<ModalState>({ open: false, mode: 'create' });
  const [scrollToId, setScrollToId] = useState<string | null>(null);
  const [expandedParentId, setExpandedParentId] = useState<string | null>(null);

  // Prepara a lista de pais disponíveis para o formulário, evitando referências circulares
  const availableParents = React.useMemo(() => {
    const flatList = flattenCategoriesForSelect(categories);
    if (modalState.mode === 'edit' && modalState.category) {
      const descendantIds = getDescendantIds(modalState.category);
      return flatList.filter(cat => !descendantIds.includes(cat.id));
    }
    return flatList;
  }, [categories, modalState.mode, modalState.category]);

  const fetchCategories = useCallback(async () => {
    if (!selectedEntity) { // Corrigido
      setError("Nenhuma entidade selecionada.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await listCategories(selectedEntity); // Corrigido
      setCategories(data);
    } catch (err) {
      setError("Falha ao carregar as categorias.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedEntity]); // Corrigido

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenModal = (mode: 'create' | 'edit', category?: ICategory, parentId?: string | null) => {
    setModalState({ open: true, mode, category, parentId });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, mode: 'create', category: undefined, parentId: undefined });
  };

  const handleSubmit = async (values: ICategoryFormValues) => {
    if (!selectedEntity) { // Corrigido
      setSnackbar({ open: true, message: "Erro: Nenhuma entidade selecionada.", severity: 'error' });
      return;
    }

    // Normaliza o parent_id para null se for uma string vazia, garantindo consistência.
    const finalValues = { ...values, parent_id: values.parent_id || null };

    try {
      if (modalState.mode === 'create') {
        const payload = {
          ...finalValues,
          entity_id: selectedEntity, // Corrigido
        };
        await createCategory(payload);
        setSnackbar({ open: true, message: "Categoria criada com sucesso!", severity: 'success' });
        handleCloseModal();
        fetchCategories();
      } else if (modalState.mode === 'edit' && modalState.category) {
        const initialParentId = modalState.category.parent_id || null;
        const parentHasChanged = initialParentId !== finalValues.parent_id;

        const updatedCategoryData = await updateCategory(modalState.category.id, finalValues, selectedEntity);
        
        if (parentHasChanged) {
          // Se o pai mudou, a estrutura da árvore mudou. Recarregar é a única forma segura.
          // Se a categoria foi movida para um novo pai, rolamos para o pai.
          setScrollToId(finalValues.parent_id || modalState.category.id);
          setExpandedParentId(finalValues.parent_id); // Memoriza o ID do novo pai para expandi-lo
          fetchCategories();
        } else {
          // Se o pai não mudou, atualizamos localmente para evitar o salto de rolagem.
          // e rolamos para a própria categoria editada.
          setScrollToId(modalState.category.id);
          const updateCategoryInTree = (nodes: ICategory[], updatedCategory: ICategory): ICategory[] => {
            return nodes.map(node => {
              if (node.id === updatedCategory.id) {
                return { ...node, ...updatedCategory };
              }
              if (node.children && node.children.length > 0) {
                return { ...node, children: updateCategoryInTree(node.children, updatedCategory) };
              }
              return node;
            });
          };
          setCategories(currentCategories => updateCategoryInTree(currentCategories, updatedCategoryData));
        }
        
        setSnackbar({ open: true, message: "Categoria atualizada com sucesso!", severity: 'success' });
        handleCloseModal();
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: `Erro ao salvar categoria.`, severity: 'error' });
    }
  };

  const handleDelete = async (categoryId: string) => {
    if (!selectedEntity || !window.confirm("Tem certeza que deseja excluir esta categoria? Isso não poderá ser desfeito.")) { // Corrigido
      return;
    }
    try {
      await deleteCategory(categoryId, selectedEntity); // Corrigido
      setSnackbar({ open: true, message: "Categoria excluída com sucesso!", severity: 'success' });
      fetchCategories();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: `Erro ao excluir categoria.`, severity: 'error' });
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Gerenciamento de Categorias
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('create', undefined, null)}>
          Nova Categoria Principal
        </Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      
      {!loading && !error && (
        <List>
          {categories.map((category) => (
            <CategoryNode 
              key={category.id} 
              category={category} 
              level={1}
              onEdit={(cat) => handleOpenModal('edit', cat)}
              onDelete={handleDelete}
              onAddSubcategory={(parentId) => handleOpenModal('create', undefined, parentId)}
              scrollToId={scrollToId}
              expandedParentId={expandedParentId}
              onScrolled={() => {
                setScrollToId(null);
                setExpandedParentId(null);
              }}
            />
          ))}
        </List>
      )}

      <CategoryForm
        open={modalState.open}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        initialValues={
          modalState.mode === 'edit' && modalState.category
            ? {
                category: modalState.category.category,
                description: modalState.category.description,
                icon: modalState.category.icon,
                parent_id: modalState.category.parent_id || null, // Garante que o valor inicial do pai esteja correto
              }
            : { category: '', description: '', icon: null, parent_id: modalState.parentId || null }
        }
        title={modalState.mode === 'create' ? (modalState.parentId ? 'Nova Subcategoria' : 'Nova Categoria Principal') : 'Editar Categoria'}
        availableParents={availableParents}
      />

      {snackbar && (
        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(null)}>
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Paper>
  );
};

export default InformationCategories;
